// Modal component styled for mfe
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import themeable from 'zignal-landscape/lib/themeable';
import { windowOpener } from '../../helpers/windowOpener';
import noop from 'lodash/noop';
import keys from 'lodash/keys';
import reduce from 'lodash/reduce';
import classnames from 'classnames';
import Save1Icon from 'zignal-landscape/lib/Icon/Save1Icon';
import SearchInput from 'zignal-landscape/lib/SearchInput';
import OutsideAlerter from '../../components/OutsideAlerter/OutsideAlerter';

const ITEM_HEIGHT = 44; // Height of each item in the dropdown
const MAX_ITEMS_INDEX = 9; // Maximum item index in the dropdown

const Width18 = () => <div style={{width: 18}} />;

const ListItem = ({styles, itemTitle, ItemIcon, item, isFocused, setFlyoutSummary, setFocusedIndex, onClick}) => {
  const ref = useRef(null);
  const [shouldSetFlyout, setShouldSetFlyout] = useState(false);
  const { summary } = item;

  useEffect(() => {
    if (ref.current && item.summary && (isFocused || shouldSetFlyout)) {
      // timeout needed here to handle flyout position correctly
      setTimeout(() => {
        const rect = ref.current?.getBoundingClientRect() || {};
        setFlyoutSummary({top: rect.top, item});
      }, 0);
    }
  }, [ref, item, isFocused, setFlyoutSummary, shouldSetFlyout]);

  const onMouseOver = useCallback(() => {
    setFocusedIndex(-1);
    if (summary) {
      setShouldSetFlyout(!shouldSetFlyout);
    }
  } , [summary, shouldSetFlyout, setShouldSetFlyout, setFocusedIndex]);

  const smallClassNames = classnames({
    [styles.MfeSearchDropDownInnerLink]: true,
    [styles.MfeSearchDropDownInnerSmallLink]: true,
    [styles.MfeSearchDropDownInnerLinkFocused]: isFocused
  });

  return (
    <div
      ref={ref}
      className={smallClassNames}
      onClick={onClick}
      onMouseOver={onMouseOver}
    >
      { ItemIcon }
      <div className={styles.MfeSearchDropDownInnerTitle}>{itemTitle}</div>
    </div>
  );
};

const Flyout = ({styles, top, item, className}) => {
  const { title, name, summary } = item;
  const itemTitle = title || name;
  const summaryMap = reduce(summary, (acc, item) => ({ ...acc, ...item }), {}) || {};
  const summaryKeys = keys(summaryMap);
  const onMouseDown = (e) => {e.preventDefault(); e.stopPropagation();};
  return (<div style={{top: top - 178}} className={classnames(styles.MfeSearchDropDownFlyout, className)} onMouseDown={onMouseDown}>
    <div className={styles.MfeSearchDropDownFlyoutInner}>
      <div className={styles.MfeSearchDropDownFlyoutTitle}>{itemTitle}</div>
      <div className={styles.MfeSearchDropDownFlyoutBody}>
        {summaryKeys.map((key) => (
          <div key={key} className={styles.MfeSearchDropDownFlyoutBodyItem}>
            <span className={styles.MfeSearchDropDownFlyoutBodyKey}>{key}:</span>{summaryMap[key]}
          </div>
        ))}
      </div>
    </div>
  </div>);
};

const DEFAULT_STYLES = {};

const MfeSearchDropDownInner = ({
  styles = DEFAULT_STYLES,
  placeholder,
  items = [{title: 'Link 1', id: 1}],
  activeItemId = null,
  isSearchable = false,
  isSmall = false,
  positionLeft = false,
  onItemsClick = noop,
  onClose = noop
}) => {
  const [flyoutSummary, setFlyoutSummary] = useState(null);
  const inputRef = useRef(null);

  const [focusedIndex, setFocusedIndex] = useState(-1); // Track the currently focused item
  const [search, setSearch] = useState('');

  const searchedItems = search ? items.filter(({title, name}) => (title || name).toLowerCase().includes(search.toLowerCase())) : items;
  const scrollableDivRef = useRef(null);

  // start with input focused
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // handle scroll position
  useEffect(() => {
    if (scrollableDivRef.current) {
      const itemIndex = searchedItems.findIndex(({id}) => id === activeItemId);
      if (focusedIndex >= 0 && searchedItems.length > MAX_ITEMS_INDEX) {
        const scrollTop = scrollableDivRef.current.scrollTop;
        if (focusedIndex === 0 && scrollableDivRef.current.scrollTop !== 0) {
          scrollableDivRef.current.scrollTop = 0;
        } else if ((scrollTop / ITEM_HEIGHT) < focusedIndex - MAX_ITEMS_INDEX) {
          scrollableDivRef.current.scrollTop = (focusedIndex - MAX_ITEMS_INDEX) * ITEM_HEIGHT;
        } else if ((scrollTop / ITEM_HEIGHT) > focusedIndex) {
          scrollableDivRef.current.scrollTop = ((focusedIndex - 1) * ITEM_HEIGHT) + 24;
        }
      } else if (scrollableDivRef.current && itemIndex > MAX_ITEMS_INDEX) {
        scrollableDivRef.current.scrollTop = (itemIndex - MAX_ITEMS_INDEX) * ITEM_HEIGHT;
      }
    }
  }, [scrollableDivRef, searchedItems, focusedIndex]);

  // Handle search input changes
  const onSearch = useCallback((search) => {
    setFocusedIndex(-1);
    setFlyoutSummary(null);
    setSearch(search);
  }, [setFocusedIndex, setFlyoutSummary, setSearch]);

  // Handle arrow keys and enter key for dropdown
  const handleKeyDown = useCallback((e) => {
    const itemIndex = searchedItems.findIndex(({id}) => id === activeItemId);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (focusedIndex === -1 && itemIndex >= 0) {
        setFocusedIndex((itemIndex + 1) % searchedItems.length);
      } else {
        setFocusedIndex((prevIndex) => (prevIndex + 1) % searchedItems.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (focusedIndex === -1 && itemIndex >= 0) {
        setFocusedIndex((itemIndex - 1 + searchedItems.length) % searchedItems.length);
      } else if (focusedIndex === -1) {
        setFocusedIndex(searchedItems.length - 1);
      } else {
        setFocusedIndex((prevIndex) => (prevIndex - 1 + searchedItems.length) % searchedItems.length);
      }
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      const selectedItem = searchedItems[focusedIndex];
      if (selectedItem) {
        onClose();
        (selectedItem.onItemClick || onItemsClick)(null, selectedItem);
      }
    }
  }, [placeholder, focusedIndex, searchedItems, onClose, setFocusedIndex, onItemsClick]);

  const flyout = flyoutSummary && <Flyout
    styles={styles}
    {...flyoutSummary}
  />;

  const mfeSearchDropDownClassNames = {
    [styles.MfeSearchDropDownInner]: true,
    [styles.isSmall]: isSmall,
    [styles.positionLeft]: positionLeft
  };

  return (<OutsideAlerter onClick={onClose} stopPropagation>
    <div
      className={classnames(mfeSearchDropDownClassNames)}
      onMouseLeave={() => setFlyoutSummary(null)}
      onKeyDown={handleKeyDown}
    >
      {flyout}
      { isSearchable &&
        <div className={styles.MfeSearchDropDownInnerSearch} onMouseOver={() => setFlyoutSummary(null)}>
          <SearchInput mfeMode placeholder={placeholder} onChange={onSearch} innerRef={inputRef} />
        </div>
      }
      <div ref={scrollableDivRef} className={styles.MfeSearchDropDownInnerItems}>
        {searchedItems.map((item, index) => {
          const { title, name, id, subtitle, link, icon: Icon, onItemClick, newIcon } = item;
          const itemTitle = title || name;
          const clickHandler = onItemClick ? onItemClick : onItemsClick;
          const onClick = (e) => {
            // Allow Cmd+click (Mac) or Ctrl+click (Win/Linux) to open in new tab
            if (link && (e.metaKey || e.ctrlKey)) {
              return;
            }

            if (!link) {
              // Non-link items: stop propagation, close and handle
              e.stopPropagation();
              onClose();
              clickHandler(e, item);
            } else if (link === '/auth/logout/') {
              // Logout: Let Backbone handle it (calls logout() method which clears session)
              e.preventDefault();
              e.stopPropagation();
              onClose();
              clickHandler(e, item);
            } else if (link.startsWith('http://') || link.startsWith('https://')) {
              // External links: prevent default and handle manually with confirm dialog
              e.preventDefault();
              e.stopPropagation();

              // Show confirm dialog and open manually if user confirms
              if (confirm('You are about to leave the Zignal Labs domain and navigate to a different one. If this is not your intention you can select the Cancel option')) {
                windowOpener(link);
              }

              // Close dropdown after dialog
              setTimeout(() => {
                onClose();
              }, 0);
            } else {
              // Internal links: prevent browser navigation, close dropdown, let Backbone handle routing
              e.preventDefault();
              e.stopPropagation();
              onClose();
              clickHandler(e, item);
            }
          };
          const iconSize = newIcon ? 24 : 18;
          let ItemIcon = Icon ? <Icon height={iconSize} width={iconSize} /> : null;
          if (id) {
            ItemIcon = activeItemId === id ? <Save1Icon height={iconSize} width={iconSize} /> : <Width18 />;
          }

          // Add ref for each item and highlight the focused item
          const isFocused = index === focusedIndex;
          const itemClassNames = classnames(styles.MfeSearchDropDownInnerLink, {
            [styles.MfeSearchDropDownInnerLinkFocused]: isFocused
          });
          // Determine if link is external or logout (both open in new tab)
          const isExternalLink = link && (link.startsWith('http://') || link.startsWith('https://'));
          const isLogout = link === '/auth/logout/';

          // In development, logout needs to point to actual backend (staging)
          // In production/staging, use the relative URL which works on the same domain
          const isDevelopment = process.env.NODE_ENV === 'development';
          const logoutUrl = isDevelopment
            ? 'https://client.staging.zignallabs.com/auth/logout/'
            : '/auth/logout/';

          // For rendering: use the actual URL we'll navigate to
          const effectiveLink = isLogout ? logoutUrl : link;
          const linkTarget = (isExternalLink || isLogout) ? '_blank' : undefined;

          return link ? (
            <a
              href={effectiveLink}
              key={title}
              target={linkTarget}
              className={itemClassNames}
              onClick={onClick}
            >
              <div className={styles.MfeSearchDropDownInnerTitle}>{itemTitle}</div>
              { subtitle ? <div className={styles.MfeSearchDropDownInnerSubtitle}>{subtitle}</div>: null }
            </a>
          ) : (
            <ListItem
              key={id || itemTitle}
              styles={styles}
              isFocused={isFocused}
              itemTitle={itemTitle}
              ItemIcon={ItemIcon}
              item={item}
              setFlyoutSummary={setFlyoutSummary}
              setFocusedIndex={setFocusedIndex}
              onClick={onClick}
            />
          );
        })}
      </div>
    </div>
  </OutsideAlerter>);
};

MfeSearchDropDownInner.propTypes = {
  styles: PropTypes.object.isRequired,
  items: PropTypes.array,
  activeItemId: PropTypes.string,
  isSearchable: PropTypes.bool,
  isSmall: PropTypes.bool,
  positionLeft: PropTypes.bool,
  onItemsClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

/* Higher order component that handles the click events for the dropdown. The className is passed to this component for flexibility */
const MfeSearchDropDown = ({
  styles,
  children,
  testId = '',
  className = '',
  classNameActive = '',
  activeItem,
  disabled = false,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const onMouseDown = useCallback(() => {
    setOpen(true);
  }, []);
  const onClick = useCallback(() => {
    setOpen(true);
  }, []);
  const onClose = useCallback(() => {
    flushSync(() => {
      setOpen(false);
    });
  }, []);
  const classNames = classnames({
    [styles.MfeSearchDropDown]: true,
    [className]: !!className,
    [classNameActive]: !!classNameActive && open
  });
  const testIdProp = testId ? { 'data-cy': testId } : {};
  return (<div
    {...testIdProp}
    className={classnames(classNames)}
    onMouseDown={disabled ? noop : onMouseDown}
    onClick={disabled ? noop : onClick}
  >
    { children }
    { open ?
      <MfeSearchDropDownInner
        styles={styles}
        {...rest}
        onClose={onClose}
      /> : null
    }
    { !open && activeItem && activeItem.summary &&
      <Flyout
        styles={styles}
        className={styles.MfeSearchDropDownFlyoutActiveItem}
        top={202}
        item={activeItem}
      />
    }
  </div>);
};

MfeSearchDropDown.propTypes = {
  children: PropTypes.node,
  testId: PropTypes.string,
  className: PropTypes.string,
  classNameActive: PropTypes.string,
};

export default themeable(MfeSearchDropDown);
